/**
 * Картинка из буфера обмена и из перетаскивания.
 *
 * Скриншот, снятый системой, приезжает в Ctrl+V одним файлом image/png;
 * файл, скопированный в проводнике, — тем же способом. Перетаскивание
 * отдаёт тот же DataTransfer, поэтому обе дороги читаются одним разбором.
 */

/** То немногое, что нужно от DataTransfer: так разбор проверяется без браузера. */
export type ClipboardPayload = {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<{ kind: string; type: string; getAsFile: () => File | null }> | null;
  types?: readonly string[] | null;
};

export function imageFromClipboard(data: ClipboardPayload | null | undefined): File | null {
  if (!data) return null;
  const file = Array.from(data.files ?? []).find((f) => f.type.startsWith('image/'));
  if (file) return file;
  // Safari кладёт вставленный скриншот только в items, files у него пуст.
  const item = Array.from(data.items ?? []).find(
    (i) => i.kind === 'file' && i.type.startsWith('image/'),
  );
  return item?.getAsFile() ?? null;
}

/** Есть ли в буфере текст: в поле ввода он важнее картинки. */
export function hasPlainText(data: ClipboardPayload | null | undefined): boolean {
  return Array.from(data?.types ?? []).some((t) => t === 'text/plain' || t === 'text/uri-list');
}

export type PasteCandidate = {
  id: string;
  /** Занятое поле перебивать не спешим: сначала ищем пустое. */
  hasFile: boolean;
  /** Поле внутри открытого диалога. */
  inDialog: boolean;
};

/**
 * Куда положить вставленную картинку.
 *
 * Первым делом — поле, которого последним касались: человек показал
 * пальцем, спорить не с чем. Дальше первое пустое: вставляют, как
 * правило, чтобы заполнить, а не чтобы заменить. Диалог перекрывает
 * карточку, поэтому, если открыт он, выбор идёт только между его полями —
 * иначе картинка улетела бы в поле, которого на экране сейчас не видно.
 *
 * Кандидаты приходят в порядке обхода страницы, поэтому «первое пустое» —
 * это верхнее пустое, а не случайное.
 */
export function pickPasteTarget(
  candidates: readonly PasteCandidate[],
  armedId: string | null,
): string | null {
  const inDialog = candidates.filter((c) => c.inDialog);
  const pool = inDialog.length > 0 ? inDialog : candidates;
  const armed = pool.find((c) => c.id === armedId);
  if (armed) return armed.id;
  return (pool.find((c) => !c.hasFile) ?? pool[0])?.id ?? null;
}
